import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const requests = new Map();
const verifyOnce = (token) => {
  if (!requests.has(token)) requests.set(token, axios.post(`${API}/subscribe/verify`, { token }));
  return requests.get(token);
};

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ status: "loading", email: "", error: "" });

  useEffect(() => {
    if (!token) {
      setState({ status: "error", email: "", error: "This verification link isn't valid." });
      return undefined;
    }
    let live = true;
    verifyOnce(token)
      .then((r) => { if (live) setState({ status: "verified", email: r.data?.email ?? "", error: "" }); })
      .catch((e) => {
        const d = e?.response?.data?.detail;
        if (live) setState({ status: "error", email: "", error: typeof d === "string" ? d : "We couldn't verify this link. Please try again." });
      });
    return () => { live = false; };
  }, [token]);

  return (
    <div data-testid="verify-email-page" className="mx-auto flex min-h-[70vh] max-w-2xl flex-col justify-center px-5 py-20 sm:px-8">
      {state.status === "loading" && (
        <p className="flex items-center gap-3 text-lg font-semibold" data-testid="verify-email-loading"><Loader2 className="h-5 w-5 animate-spin" />Verifying your email…</p>
      )}
      {state.status === "verified" && (
        <div data-testid="verify-email-success">
          <CheckCircle2 className="h-12 w-12 text-[#16A34A]" data-testid="verify-email-success-icon" />
          <h1 className="mt-6 font-display text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">Your email is verified</h1>
          <p className="mt-5 text-base text-[#3a3836] sm:text-lg">
            <strong data-testid="verify-email-address">{state.email}</strong> has been verified and added to the Cresway Media mailing list. You'll hear from us with updates, announcements and news.
          </p>
        </div>
      )}
      {state.status === "error" && (
        <div data-testid="verify-email-error">
          <XCircle className="h-12 w-12 text-[#0A0A0A]" />
          <h1 className="mt-6 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">Link not valid</h1>
          <p className="mt-5 text-base text-[#3a3836] sm:text-lg" data-testid="verify-email-error-message">{state.error}</p>
        </div>
      )}
      {state.status !== "loading" && (
        <Link to="/" data-testid="verify-email-home-link" className="mt-10 inline-flex w-fit border-b-2 border-[#E62314] pb-1 text-lg font-extrabold transition-colors hover:text-[#E62314]">
          Back to the website →
        </Link>
      )}
    </div>
  );
}
